import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addTenantAccount, deleteTenantAccount, updateTenantAccount } from '../../api/endpoints';
import { ApiError } from '../../api/http';
import { queryKeys } from '../../api/queryKeys';
import type { AdminFlowisAccount } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { AccountIdentifierFields } from './AccountIdentifierFields';
import { identifiersFrom, identifiersToRequest, type IdentifierFormState } from './accountIdentifiers';
import { useAccountAssignment } from './useAccountAssignment';

interface AccountDialogProps {
  tenantGid: string;
  account: AdminFlowisAccount | null;
  onClose: () => void;
}

function AccountDialog({ tenantGid, account, onClose }: AccountDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [accountId, setAccountId] = useState(() => account?.accountId ?? '');
  const [name, setName] = useState(() => account?.name ?? '');
  const [enabled, setEnabled] = useState(() => account?.enabled ?? true);
  const [identifiers, setIdentifiers] = useState<IdentifierFormState>(() => identifiersFrom(account));

  const mutation = useMutation({
    mutationFn: () =>
      account
        ? updateTenantAccount(account.accountGid, {
            name: name.trim(),
            enabled,
            ...identifiersToRequest(identifiers),
          })
        : addTenantAccount(tenantGid, {
            accountId: accountId.trim(),
            // Optional: without one the account keeps the name Flowis holds for it.
            name: name.trim() === '' ? null : name.trim(),
            enabled,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.tenantAccounts.saved'));
      onClose();
    },
    onError: toastError,
  });

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {account ? t('admin.tenantAccounts.dialog.editTitle') : t('admin.tenantAccounts.dialog.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('admin.accounts.columns.accountId')}
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
            required
            disabled={account !== null}
            helperText={account ? t('admin.accounts.accountIdLocked') : t('admin.accounts.accountIdHelp')}
          />
          <TextField
            label={t('columns.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required={account !== null}
            helperText={
              account ? t('admin.accounts.nameOperatorsHelp') : t('admin.accounts.nameOptionalHelp')
            }
          />
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
            label={t('states.enabled')}
          />
          {/* Only when editing: a new registration reads its identifiers from Flowis rather than here. */}
          {account ? (
            <AccountIdentifierFields value={identifiers} onChange={setIdentifiers} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('admin.tenantAccounts.dialog.readFromFlowis')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || (account ? name.trim() === '' : accountId.trim() === '')}
        >
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TenantAccountsTab({
  tenantGid,
  accounts,
}: {
  tenantGid: string;
  accounts: AdminFlowisAccount[];
}) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [editing, setEditing] = useState<AdminFlowisAccount | 'new' | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminFlowisAccount | null>(null);

  const { unassign } = useAccountAssignment();

  const toggleMutation = useMutation({
    mutationFn: (account: AdminFlowisAccount) =>
      updateTenantAccount(account.accountGid, {
        name: account.name,
        enabled: !account.enabled,
        // A full replace, so the identifiers have to be sent back or they would be cleared.
        ...identifiersToRequest(identifiersFrom(account)),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
    },
    onError: toastError,
  });

  const deleteMutation = useMutation({
    mutationFn: (accountGid: string) => deleteTenantAccount(accountGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.tenantAccounts.removed'));
      setPendingDelete(null);
    },
    onError: (error) => {
      // 409 means something already points at the account, which disabling solves and deleting cannot.
      if (error instanceof ApiError && error.status === 409) {
        toastError(
          new ApiError(409, {
            detail: `${error.problem?.detail ?? t('admin.tenantAccounts.usedFallback')} ${t('admin.tenantAccounts.usedConflictSuffix')}`,
          }),
        );
      } else {
        toastError(error);
      }
      setPendingDelete(null);
    },
  });

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('admin.tenantAccounts.add')}
        </Button>
      </Box>

      <Alert severity="info">
        <Stack spacing={1}>
          {/* How an account comes to exist at all — the part that happens in Flowis, not here. */}
          <Typography variant="body2">{t('admin.tenantAccounts.flowisSetup')}</Typography>
          <Typography variant="body2">{t('admin.tenantAccounts.note')}</Typography>
        </Stack>
      </Alert>

      {accounts.length === 0 ? (
        <EmptyBlock message={t('admin.tenantAccounts.empty')} />
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('columns.name')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.participant')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.accountId')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.ico')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.dic')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.icdph')}</TableCell>
                  <TableCell>{t('columns.enabled')}</TableCell>
                  <TableCell align="right">{t('columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.accountGid} hover>
                    <TableCell>{account.name}</TableCell>
                    {/* The address Peppol knows this sender by — the identifier that matters most. */}
                    <TableCell sx={{ fontFamily: 'monospace' }}>{account.participantId ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{account.accountId}</TableCell>
                    <TableCell>{account.registrationNumber ?? '—'}</TableCell>
                    <TableCell>{account.taxId ?? '—'}</TableCell>
                    <TableCell>{account.vatId ?? '—'}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={account.enabled}
                        disabled={toggleMutation.isPending}
                        onChange={() => toggleMutation.mutate(account)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('actions.edit')}>
                        <IconButton size="small" onClick={() => setEditing(account)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin.accounts.unassignTooltip')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={unassign.isPending}
                            onClick={() => unassign.mutate({ accountGid: account.accountGid, tenantGid })}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('actions.delete')}>
                        <IconButton size="small" onClick={() => setPendingDelete(account)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {editing !== null && (
        <AccountDialog
          tenantGid={tenantGid}
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('admin.tenantAccounts.confirmRemoveTitle')}
        message={t('admin.tenantAccounts.confirmRemoveMessage', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('actions.remove')}
        confirmColor="error"
        busy={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.accountGid)}
        onClose={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
